import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { AdminAuditTab } from '@/components/admin/AdminAuditTab'
import { CalendarsTab } from '@/components/admin/CalendarsTab'
import { PoliciesTab } from '@/components/admin/PoliciesTab'
import { RoutingTab } from '@/components/admin/RoutingTab'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'

type Tab = 'policies' | 'calendars' | 'routing' | 'audit'

/** Admin: SLA policies, business calendars, routing rules and the admin audit (plan §13 phase 5). */
export default function SlaPoliciesPage() {
  const { t } = useTranslation()
  const canSla = usePermission(PERMISSIONS.slaManage)
  const canRouting = usePermission(PERMISSIONS.routingManage)
  const [params, setParams] = useSearchParams()

  const tabs: Tab[] = [
    ...(canSla ? (['policies', 'calendars'] as const) : []),
    ...(canRouting ? (['routing'] as const) : []),
    ...(canSla || canRouting ? (['audit'] as const) : []),
  ]
  const requested = params.get('tab') as Tab | null
  const tab = tabs.find((x) => x === requested) ?? tabs[0]

  return (
    <Page>
      <PageHeading description={t('sla.eyebrow')} title={t('sla.title')} />
      {tabs.length === 0 ? (
        <p className="text-body text-ink-muted">{t('common.forbidden')}</p>
      ) : (
        <>
          <div role="tablist" className="flex gap-6 border-b border-line">
            {tabs.map((x) => (
              <button
                key={x}
                type="button"
                role="tab"
                aria-selected={x === tab}
                onClick={() => setParams(x === tabs[0] ? {} : { tab: x }, { replace: true })}
                className={cn(
                  '-mb-px rounded-t-sm border-b-2 pb-2.5 text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  x === tab ? 'border-primary font-semibold text-primary' : 'border-transparent text-ink-muted hover:text-ink',
                )}
              >
                {t(`admin.tabs.${x}`)}
              </button>
            ))}
          </div>
          {tab === 'policies' ? <PoliciesTab /> : tab === 'calendars' ? <CalendarsTab /> : tab === 'routing' ? <RoutingTab /> : <AdminAuditTab />}
        </>
      )}
    </Page>
  )
}
